import { createApi } from './core';
import {
  ProviderData,
  PDPatient,
  ProviderPageDetails,
  ProviderLocationHistory,
  CreateProviderRequest,
  UpdateProviderRequest,
  Point,
  RouteDetails,
  Segment,
} from './providers.types';

declare const healConfig: any;

function createProvidersApi() {
  return createApi({ baseURL: healConfig.apiBaseUrl + '/admin' });
}

export function fetchDoctors(serviceCode: string, marketCode: string, day?: number): Promise<ProviderData[]> {
  return createProvidersApi()
    .then((api) => api.get<ProviderData[]>('/providers', { serviceCode, marketCode, day }))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve all doctors'))));
}

export function fetchAssistants(serviceCode: string, marketCode: string, day?: number): Promise<ProviderData[]> {
  return createProvidersApi()
    .then((api) => api.get<ProviderData[]>('/providers/medical_assistants', { serviceCode, marketCode, day }))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve all assistants'))));
}

export function fetchAllProviders(): Promise<ProviderData[]> {
  return createProvidersApi()
    .then((api) => api.get<ProviderData[]>('/providers/all'))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve all providers'))));
}

export function fetchProvider(providerId: string): Promise<ProviderPageDetails> {
  return createProvidersApi()
    .then((api) => api.get<ProviderPageDetails>(`/providers/${providerId}`))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve provider details'))));
}

export function fetchPDPatients(providerId: string, offset = 0, limit = 1000): Promise<PDPatient[]> {
  return createProvidersApi()
    .then((api) => api.get<any>(`/providers/${providerId}/pd-patients`, { offset, limit }))
    .then((r) => (r.ok && r.data ? r.data.results || [] : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve provider patients'))));
}

export function createProvider(params: CreateProviderRequest): Promise<any> {
  return createProvidersApi()
    .then((api) => api.post(`/providers`, params))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to create provider'))));
}

export function updateProvider(providerId: string, params: UpdateProviderRequest): Promise<any> {
  return createProvidersApi()
    .then((api) => api.patch(`/providers/${providerId}`, params))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to update provider'))));
}

export function fetchProviderLocationHistory(providerId: string, dateStr?: string): Promise<RouteDetails> {
  const date = (dateStr && new Date(dateStr)) || new Date();
  const params = {
    startDate: date.setHours(0, 0, 0, 0),
    endDate: date.setHours(23, 59, 59, 999),
  };

  return createProvidersApi()
    .then((api) => api.get<ProviderLocationHistory[]>(`/providers/${providerId}/location`, params))
    .then((r) => (r.ok && r.data ? r.data : Promise.reject(new Error(((r.data || {}) as any).description || 'Failed to retrieve provider location history'))))
    .then((r) => processLocationEvents(r));
}

function processLocationEvents(events: ProviderLocationHistory[]): RouteDetails {
  const result: RouteDetails = { segments: [], allPoints: [] };
  let lastTimestamp = 0;
  let lastPoint: any = null;
  let segment: Segment = { points: [] };

  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.longitude === 0 || event.latitude === 0) {
      continue;
    }

    const currentPoint: Point = {
      lat: event.latitude,
      lng: event.longitude,
      timestamp: new Date(event.locationTime),
      status: event.currentStatus,
    };

    if (i !== events.length - 1) {
      const delta = (event.locationTime - lastTimestamp) / 1000 / 60;
      if (Math.abs(delta) > 5) {
        // 10 minutes
        // store the previous segment
        if (segment.points.length > 1) {
          result.segments.push(segment);
        }
        if (lastPoint !== null) {
          // add a gap segment
          segment = { points: [lastPoint, currentPoint], type: 'gap' };
          result.segments.push(segment);
        }

        // start a new segment
        segment = { points: [] };
      }
    }
    lastTimestamp = event.locationTime;

    segment.points.push(currentPoint);

    result.allPoints.push(currentPoint);
    lastPoint = currentPoint;
  }

  if (segment.points.length > 0) {
    result.segments.push(segment);
  }

  return result;
}
